'use client';

import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import {
  ShieldCheck,
  FlaskConical,
  EyeOff,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Layers,
  History,
} from 'lucide-react';
import { breadcrumbSchema } from '@/lib/utils/structured-data';
import { platformLink } from '@/lib/config';

const breadcrumb = breadcrumbSchema([
  { name: 'Home', url: 'https://www.silkerai.com' },
  { name: 'Detection Benchmarks', url: 'https://www.silkerai.com/benchmarks' },
]);

const SDK_VERSION = '1.6.8';
const BENCHMARK_DATE = '2026-07-01';

type SuiteId = 'core' | 'extended' | 'community';

type Dataset = {
  threat: string;
  policy: string;
  samples: string;
  tpr: number;
  macroTpr?: number;
  fpr: number;
  precision: number;
  confusion: { tp: number; fn: number; fp: number; tn: number };
};

type SuiteMeta = {
  id: SuiteId;
  label: string;
  description: string;
  totalSamples: number;
  ciGate: boolean;
  datasets: Dataset[];
  headline: {
    fpr: string;
    sqliXss: string;
    promptInjection: string;
    promptSub: string;
    /** Shown under headline cards - reminds that scores are lab snapshots, not guarantees. */
    caveat: string;
  };
  /** When false, hide the prompt-injection headline card (community suite). */
  showPromptInjectionHeadline?: boolean;
};

const suites: SuiteMeta[] = [
  {
    id: 'core',
    label: 'Core suite',
    description: 'Hand-curated regression gate (~210 samples). Runs on every PR in CI.',
    totalSamples: 210,
    ciGate: true,
    headline: {
      fpr: '0.0%',
      sqliXss: '100.0%',
      promptInjection: '100.0%',
      promptSub: 'Core · 210 curated samples · 0 FN this run',
      caveat: 'Measured on our hand-labeled set only - not a completeness guarantee.',
    },
    datasets: [
      {
        threat: 'Prompt injection',
        policy: 'LLM routes - block on medium+ severity, or low + override signal',
        samples: '130 (75 attack / 55 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 75, fn: 0, fp: 0, tn: 55 },
      },
      {
        threat: 'Prompt injection',
        policy: 'Non-LLM routes - block only high / critical',
        samples: '130 (75 attack / 55 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 75, fn: 0, fp: 0, tn: 55 },
      },
      {
        threat: 'SQL injection',
        policy: 'Block on detection',
        samples: '40 (20 attack / 20 benign)',
        tpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 20, fn: 0, fp: 0, tn: 20 },
      },
      {
        threat: 'XSS',
        policy: 'Block on detection',
        samples: '40 (20 attack / 20 benign)',
        tpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 20, fn: 0, fp: 0, tn: 20 },
      },
    ],
  },
  {
    id: 'extended',
    label: 'Extended suite',
    description: 'Core + 802 adversarial additions (1012 samples). Harder, stratified by category - transparency benchmark.',
    totalSamples: 1012,
    ciGate: false,
    headline: {
      fpr: '0.0%',
      sqliXss: '100.0%',
      promptInjection: '100.0%',
      promptSub: 'Extended · 1,012 curated samples · 0 FN this run',
      caveat: 'Curated adversarial set - new attack variants can always appear outside it.',
    },
    datasets: [
      {
        threat: 'Prompt injection',
        policy: 'LLM routes - block on medium+ severity, or low + override signal',
        samples: '411 (294 attack / 117 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 294, fn: 0, fp: 0, tn: 117 },
      },
      {
        threat: 'Prompt injection',
        policy: 'Non-LLM routes - block only high / critical',
        samples: '411 (294 attack / 117 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 294, fn: 0, fp: 0, tn: 117 },
      },
      {
        threat: 'SQL injection',
        policy: 'Block on detection',
        samples: '264 (129 attack / 135 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 129, fn: 0, fp: 0, tn: 135 },
      },
      {
        threat: 'XSS',
        policy: 'Block on detection',
        samples: '337 (191 attack / 146 benign)',
        tpr: 100,
        macroTpr: 100,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 191, fn: 0, fp: 0, tn: 146 },
      },
    ],
  },
  {
    id: 'community',
    label: 'Community suite',
    description:
      'Third-party payloads from PayloadsAllTheThings + HttpParamsDataset (1,494 samples). The stress test - scores are expected to be lower.',
    totalSamples: 1494,
    ciGate: false,
    showPromptInjectionHeadline: false,
    headline: {
      fpr: '0.0%',
      sqliXss: '96–98%',
      promptInjection: '—',
      promptSub: 'Community · 786 SQLi + 308 XSS attacks from public repos',
      caveat: 'Real-world payload diversity - this is the honest coverage map.',
    },
    datasets: [
      {
        threat: 'SQL injection',
        policy: 'Block on detection',
        samples: '986 (786 attack / 200 benign)',
        tpr: 97.7,
        macroTpr: 95.9,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 768, fn: 18, fp: 0, tn: 200 },
      },
      {
        threat: 'XSS',
        policy: 'Block on detection',
        samples: '508 (308 attack / 200 benign)',
        tpr: 96.1,
        macroTpr: 97.4,
        fpr: 0.0,
        precision: 100,
        confusion: { tp: 296, fn: 12, fp: 0, tn: 200 },
      },
    ],
  },
];

type HistoryEntry = {
  version: string;
  date: string;
  headline: string;
  llmTpr: number;
  nonLlmTpr: number;
  fpr: number;
};

// Version-over-version detection history (oldest at the bottom).
const history: HistoryEntry[] = [
  {
    version: '1.6.8',
    date: '2026-07-01',
    headline:
      'SQLi quick-pattern fast-path fixed to run before the length gate, plus new signatures (bare boolean probes, ORDER BY enumeration, time-based blind functions, CAST/CONVERT+SELECT, CHAR()/CHR() concatenation). Community SQLi 68.1% → 97.7% TPR at 0% FPR; core/extended unchanged at 100%.',
    llmTpr: 100,
    nonLlmTpr: 100,
    fpr: 0.0,
  },
  {
    version: '1.6.7',
    date: '2026-06-29',
    headline:
      'Community benchmark suite in CI (PayloadsAllTheThings + HttpParamsDataset, 1,494 samples). SQLi ORDER BY / GROUP BY / nested-paren blind fix. Curated extended 0 FN; community SQLi 68.1% / XSS 96.1% at 0% FPR.',
    llmTpr: 100,
    nonLlmTpr: 100,
    fpr: 0.0,
  },
  {
    version: '1.6.6',
    date: '2026-06-28',
    headline:
      'Auth endpoint safe harbor and outbound-only egress guard. Detection scores unchanged on curated suites.',
    llmTpr: 100,
    nonLlmTpr: 100,
    fpr: 0.0,
  },
  {
    version: '1.6.1',
    date: '2026-06-26',
    headline:
      'SQLi comment/hash markers now require SQL context - eliminated benign false positives on text like "mid-2024 -- note". Curated extended suite unchanged at 0% FPR.',
    llmTpr: 100,
    nonLlmTpr: 100,
    fpr: 0.0,
  },
  {
    version: '1.6.0',
    date: '2026-06-25',
    headline:
      'Closed remaining extended-suite gaps: prompt-injection non-LLM-route 97.6% → 100%, SQLi 96.9% → 100%, all four datasets at 100% TPR / 0% FPR on extended (1012 samples).',
    llmTpr: 100,
    nonLlmTpr: 100,
    fpr: 0.0,
  },
  {
    version: '1.5.2',
    date: '2026-06-22',
    headline:
      'Leetspeak/spacing/homoglyph normalization + decode-and-rescan lifted extended prompt-injection LLM-route TPR from 71.4% to 100% at 0% FPR. Core suite also 100%.',
    llmTpr: 100,
    nonLlmTpr: 97.6,
    fpr: 0.0,
  },
  {
    version: '1.5.1',
    date: '2026-06-22',
    headline:
      'Apache-2.0 license. Dual benchmark suites: core (~210, CI gate) and extended (~1012, transparency).',
    llmTpr: 71.4,
    nonLlmTpr: 71.4,
    fpr: 0.0,
  },
  {
    version: '1.5.0',
    date: '2026-06-22',
    headline:
      'Input normalization (zero-width strip + NFKC), base64/escape decode-and-rescan lifted core non-LLM prompt-injection from 76.3% to 96.0% at 0% FPR.',
    llmTpr: 96.0,
    nonLlmTpr: 96.0,
    fpr: 0.0,
  },
  {
    version: '1.3.3',
    date: '2026-06-10',
    headline:
      'Precise LLM-route policy (block on medium+ or override signal) cut LLM-route false positives from 24.4% to 0% while keeping detection high.',
    llmTpr: 94.9,
    nonLlmTpr: 76.3,
    fpr: 0.0,
  },
];

function MetricBar({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'detection' | 'fpr';
}) {
  const display = `${value.toFixed(1)}%`;
  const fill =
    tone === 'detection'
      ? 'bg-gradient-to-r from-rose-500 to-rose-400'
      : 'bg-gradient-to-r from-amber-500 to-amber-400';
  const valueColor = tone === 'detection' ? 'text-rose-400' : 'text-amber-400';
  return (
    <div>
      <div className="flex justify-between items-baseline text-xs mb-1.5">
        <span className="text-neutral-400">{label}</span>
        <span className={`font-mono ${valueColor}`}>{display}</span>
      </div>
      <div className="h-3 w-full bg-neutral-900 border border-neutral-800/50 rounded-none overflow-hidden">
        <div
          className={`h-full ${fill}`}
          style={{ width: `${Math.max(value, 0)}%` }}
        />
      </div>
    </div>
  );
}

function DatasetChart({ d }: { d: Dataset }) {
  return (
    <div
      className="bg-neutral-900/30 border border-neutral-800/50 rounded-none p-5"
      role="img"
      aria-label={`${d.threat}, ${d.policy}. Detection rate ${d.tpr.toFixed(
        1
      )} percent, false-positive rate ${d.fpr.toFixed(
        1
      )} percent, precision ${d.precision} percent, ${d.samples} samples.`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">{d.threat}</h3>
          <p className="text-xs text-neutral-500 mt-0.5">{d.policy}</p>
        </div>
        <span className="text-[11px] font-mono text-neutral-500 whitespace-nowrap">
          {d.samples}
        </span>
      </div>
      <div className="space-y-3">
        <MetricBar label="Detection rate (TPR)" value={d.tpr} tone="detection" />
        <MetricBar label="False-positive rate (FPR)" value={d.fpr} tone="fpr" />
      </div>
      <div className="mt-4 pt-3 border-t border-neutral-800/50 grid grid-cols-4 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">TP</div>
          <div className="text-sm font-mono text-white">{d.confusion.tp}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">FN</div>
          <div className="text-sm font-mono text-white">{d.confusion.fn}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">FP</div>
          <div className="text-sm font-mono text-white">{d.confusion.fp}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">TN</div>
          <div className="text-sm font-mono text-white">{d.confusion.tn}</div>
        </div>
      </div>
    </div>
  );
}

export default function BenchmarksPage() {
  const [activeSuiteId, setActiveSuiteId] = useState<SuiteId>('extended');
  const activeSuite = suites.find((s) => s.id === activeSuiteId) ?? suites[0];
  const datasets = activeSuite.datasets;
  const h = activeSuite.headline;

  return (
    <div className="min-h-screen bg-black">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
      />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main className="max-w-4xl mx-auto px-6 py-32">
        {/* Hero / intro */}
        <div className="text-center mb-20" data-scroll-animate="fade-up">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
            Detection Benchmarks
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="text-white">Radical transparency</span>
            <br />
            <span className="bg-gradient-to-r from-neutral-200 via-neutral-400 to-neutral-600 bg-clip-text text-transparent">
              in security detection
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-xl mx-auto">
            We publish how our detection performs - including what it misses.
          </p>
          <p className="text-sm text-neutral-500 max-w-2xl mx-auto mt-4 leading-relaxed">
            Three reproducible suites from our open SDK: a{' '}
            <strong className="text-neutral-300">core</strong> regression gate (CI on every PR), an{' '}
            <strong className="text-neutral-300">extended</strong> curated adversarial set, and a{' '}
            <strong className="text-neutral-300">community</strong> suite built from public payload
            repos (PayloadsAllTheThings, HttpParamsDataset). Scores are lab snapshots - never a
            guarantee that every future attack is caught. Measured on{' '}
            <code className="text-rose-400 font-mono">@silker-ai/agent</code> v{SDK_VERSION} · {BENCHMARK_DATE}.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {suites.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSuiteId(s.id)}
                className={`px-4 py-2 text-sm border transition-colors cursor-pointer ${
                  activeSuiteId === s.id
                    ? 'border-rose-500/50 bg-rose-500/10 text-white'
                    : 'border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-200'
                }`}
              >
                {s.label}
                <span className="ml-2 text-xs font-mono text-neutral-500">{s.totalSamples} samples</span>
                {s.ciGate && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-400/80">CI</span>
                )}
              </button>
            ))}
          </div>
          <p className="text-xs text-neutral-600 max-w-xl mx-auto mt-3">{activeSuite.description}</p>
        </div>

        <div className="space-y-8">
          {/* Headline metrics */}
          <section data-scroll-animate="fade-up">
            <div
              className={`grid grid-cols-1 gap-4 ${
                activeSuite.showPromptInjectionHeadline === false
                  ? 'md:grid-cols-2'
                  : 'md:grid-cols-3'
              }`}
            >
              <div className="rounded-none border border-rose-500/20 bg-neutral-950/50 backdrop-blur-xl p-6">
                <div className="text-4xl font-bold bg-gradient-to-r from-rose-400 to-rose-300 bg-clip-text text-transparent">
                  {h.fpr}
                </div>
                <div className="text-sm font-medium text-white mt-2">
                  False positives
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  {activeSuite.label} · measured FPR on this run
                </p>
              </div>
              <div className="rounded-none border border-rose-500/20 bg-neutral-950/50 backdrop-blur-xl p-6">
                <div className="text-4xl font-bold bg-gradient-to-r from-rose-400 to-rose-300 bg-clip-text text-transparent">
                  {h.sqliXss}
                </div>
                <div className="text-sm font-medium text-white mt-2">
                  SQLi &amp; XSS TPR
                </div>
                <p className="text-xs text-neutral-500 mt-1">{h.promptSub}</p>
              </div>
              {activeSuite.showPromptInjectionHeadline !== false && (
                <div className="rounded-none border border-rose-500/20 bg-neutral-950/50 backdrop-blur-xl p-6">
                  <div className="text-4xl font-bold bg-gradient-to-r from-rose-400 to-rose-300 bg-clip-text text-transparent">
                    {h.promptInjection}
                  </div>
                  <div className="text-sm font-medium text-white mt-2">
                    Prompt-injection TPR
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    Curated suite only · LLM + non-LLM routes
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-amber-400/90 mt-4 text-center max-w-2xl mx-auto leading-relaxed">
              {h.caveat} No benchmark proves complete coverage - new variants always appear outside any fixed sample set.
            </p>
          </section>

          {/* Results charts */}
          <section
            className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8"
            data-scroll-animate="fade-up"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-rose-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Results</h2>
            </div>
            <div className="flex items-center gap-5 mb-6 text-xs text-neutral-400">
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-none bg-gradient-to-r from-rose-500 to-rose-400" />
                Detection rate (TPR)
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-none bg-gradient-to-r from-amber-500 to-amber-400" />
                False-positive rate (FPR)
              </span>
              <span className="text-neutral-600 hidden sm:inline">· 0–100% scale</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {datasets.map((d, i) => (
                <DatasetChart key={`${d.threat}-${i}`} d={d} />
              ))}
            </div>

            {/* Summary table */}
            <div className="mt-8 overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <caption className="sr-only">
                  Detection benchmark summary: detection rate, false-positive
                  rate, and precision per threat and policy.
                </caption>
                <thead>
                  <tr className="border-b border-neutral-800">
                    <th scope="col" className="py-3 pr-4 font-medium text-neutral-300">Threat</th>
                    <th scope="col" className="py-3 pr-4 font-medium text-neutral-300">Policy</th>
                    <th scope="col" className="py-3 pr-4 font-medium text-neutral-300 whitespace-nowrap">Samples</th>
                    <th scope="col" className="py-3 pr-4 font-medium text-neutral-300 whitespace-nowrap">TPR</th>
                    <th scope="col" className="py-3 pr-4 font-medium text-neutral-300 whitespace-nowrap">FPR</th>
                    <th scope="col" className="py-3 font-medium text-neutral-300 whitespace-nowrap">Precision</th>
                  </tr>
                </thead>
                <tbody>
                  {datasets.map((d, i) => (
                    <tr key={`row-${i}`} className="border-b border-neutral-800/50">
                      <td className="py-3 pr-4 text-white whitespace-nowrap">{d.threat}</td>
                      <td className="py-3 pr-4 text-neutral-400">{d.policy}</td>
                      <td className="py-3 pr-4 text-neutral-400 font-mono text-xs whitespace-nowrap">{d.samples}</td>
                      <td className="py-3 pr-4 font-mono text-rose-400 whitespace-nowrap">{d.tpr.toFixed(1)}%</td>
                      <td className="py-3 pr-4 font-mono text-amber-400 whitespace-nowrap">{d.fpr.toFixed(1)}%</td>
                      <td className="py-3 font-mono text-neutral-200 whitespace-nowrap">{d.precision}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Benchmark history */}
          <section
            className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8"
            data-scroll-animate="fade-up"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
                <History className="w-5 h-5 text-rose-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Benchmark history</h2>
            </div>
            <p className="text-sm text-neutral-400 mb-6 leading-relaxed">
              How detection has changed over time - prompt-injection detection
              rate (TPR) per release, at a constant 0% false-positive rate.
            </p>
            <div className="space-y-4">
              {history.map((h) => (
                <div
                  key={h.version}
                  className="bg-neutral-900/30 border border-neutral-800/50 rounded-none p-5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-white">
                        v{h.version}
                      </span>
                      <span className="text-xs text-neutral-500 font-mono">
                        {h.date}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span className="text-neutral-400">
                        LLM <span className="text-rose-400">{h.llmTpr.toFixed(1)}%</span>
                      </span>
                      <span className="text-neutral-400">
                        non-LLM <span className="text-rose-400">{h.nonLlmTpr.toFixed(1)}%</span>
                      </span>
                      <span className="text-neutral-400">
                        FPR <span className="text-amber-400">{h.fpr.toFixed(1)}%</span>
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    {h.headline}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* What we DON'T catch */}
          <section
            className="rounded-none border border-amber-500/20 bg-neutral-950/50 backdrop-blur-xl p-8"
            data-scroll-animate="fade-up"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-none bg-amber-500/5 border border-amber-500/20 flex items-center justify-center">
                <EyeOff className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">What we don&apos;t catch</h2>
            </div>
            <p className="text-sm text-neutral-400 mb-6 leading-relaxed">
              Honesty is part of the product. Here is what these benchmarks show
              we currently miss, and why.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-base font-medium text-white mb-2">
                  Community suite: the real stress test
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  On the <strong className="text-white">community</strong> suite (1,494 samples from{' '}
                  <a
                    href="https://github.com/swisskyrepo/PayloadsAllTheThings"
                    className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PayloadsAllTheThings
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://github.com/Morzeux/HttpParamsDataset"
                    className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HttpParamsDataset
                  </a>
                  ), SQLi TPR is <strong className="text-white">97.7%</strong> (18 misses) and XSS is{' '}
                  <strong className="text-white">96.1%</strong> (12 misses) at 0% FPR. This is the
                  number we trust for honest coverage - curated suites can look perfect while
                  community payloads expose gaps.
                </p>
              </div>

              <div>
                <h3 className="text-base font-medium text-white mb-2">
                  Curated suites: regression direction, not proof
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  The <strong className="text-white">extended</strong> suite (1,012 hand-labeled samples)
                  currently shows 0 false negatives on this run - but that only means we pass our own
                  test set today. The <strong className="text-white">core</strong> suite (~210 samples)
                  runs on every PR to catch regressions fast.
                </p>
              </div>

              <div>
                <h3 className="text-base font-medium text-white mb-2">
                  We chose 0% false positives over chasing the last percent
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  We deliberately do <strong className="text-white">not</strong> special-case
                  residual misses with brittle rules that would re-introduce false positives on
                  legitimate roleplay UX.
                </p>
              </div>

              <div>
                <h3 className="text-base font-medium text-white mb-2">
                  Heuristics are a first layer, not the whole defense
                </h3>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  Heuristic detection is a fast first layer, not a complete
                  defense. It pairs with the platform&apos;s async AI verdict
                  layer and should complement - never replace - your own
                  application controls.
                </p>
              </div>
            </div>
          </section>

          {/* Methodology */}
          <section
            className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8"
            data-scroll-animate="fade-up"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-rose-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">How we verified</h2>
            </div>
            <ul className="space-y-3 text-sm text-neutral-400">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-white">Core (~210) + extended (~1012) hand-labeled samples</strong>{' '}
                  across prompt injection, SQL injection, and XSS - including
                  benign &ldquo;look-alikes&rdquo; (legit roleplay, SQL keywords
                  in normal text, HTML-ish content) to stress false positives.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  Detectors run through the <strong className="text-white">same public APIs used in production</strong> -
                  no special benchmark-only code path.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-white">Two policies reported</strong>{' '}
                  (LLM vs non-LLM) because Silker applies stricter rules on
                  AI/LLM endpoints than on plain routes.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  The benchmark is part of the <strong className="text-white">SDK test suite with CI quality gates</strong> -
                  a regression below threshold fails the build.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-white">Community suite</strong> - payloads imported from{' '}
                  <a
                    href="https://github.com/swisskyrepo/PayloadsAllTheThings"
                    className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PayloadsAllTheThings
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://github.com/Morzeux/HttpParamsDataset"
                    className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HttpParamsDataset
                  </a>
                  . Regenerate with{' '}
                  <code className="text-xs font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">npm run benchmark:import-community</code>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-white">Reproducible</strong> - run{' '}
                  <code className="text-xs font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">npm run benchmark</code>{' '}
                  (core),{' '}
                  <code className="text-xs font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">npm run benchmark:extended</code>, or{' '}
                  <code className="text-xs font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">npm run benchmark:community</code>{' '}
                  in the SDK.
                </span>
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500 border-t border-neutral-800/50 pt-4">
              <Layers className="w-3.5 h-3.5" />
              <span>
                Measured on @silker-ai/agent v{SDK_VERSION} on {BENCHMARK_DATE}.
                We&apos;ll update these results as the SDK evolves.
              </span>
            </div>
          </section>

          {/* CTA */}
          <section
            className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 text-center"
            data-scroll-animate="fade-up"
          >
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-rose-400" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Put this detection in front of your app
            </h2>
            <p className="text-sm text-neutral-400 max-w-lg mx-auto mb-6">
              Integrate the SDK in minutes, then watch verdicts and threats live
              from the dashboard.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/docs"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-none bg-rose-500/10 border border-rose-500/30 text-rose-400 hover:bg-rose-500/20 transition-colors"
              >
                <span>Read the integration docs</span>
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href={platformLink.dashboard}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-none border border-white/20 text-white hover:border-white/40 transition-colors"
              >
                <span>Open the dashboard</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
